import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Video, Calendar, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import BookingModal from "@/components/BookingModal";
import EventModal from "@/components/EventModal";
import api from "@/lib/api";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";
import ListingDescription from "@/components/ListingDescription";

const HERO = "https://static.prod-images.emergentagent.com/jobs/cc147483-69b1-437e-8d93-352c289a737f/images/69c3e51b09d578df3773fc1f5bf83b8908cfaee3362d0b20a018b9936a5c6470.png";

export default function Online() {
  const [bookSvc, setBookSvc] = useState(null);
  const [eventModal, setEventModal] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["online-offerings"],
    queryFn: () => api.get("/online/offerings").then((r) => r.data),
  });

  const services = data?.services || [];
  const events = data?.events || [];
  const enhancers = data?.enhancers || [];
  const empty = !isLoading && services.length === 0 && events.length === 0 && enhancers.length === 0;

  return (
    <div data-testid="online-page">
      <Seo
        title="Online Wellness Sessions & Events"
        description="Book virtual wellness sessions and join live online events from anywhere. Reiki, coaching, breathwork, and more — offered by our certified enhancers over Zoom."
        path="/online"
      />
      <PageHero label="Online · Virtual delivery" title="Enhance your world from anywhere."
        subtitle="No address, no commute — book live virtual sessions, join online events, and connect with Enhancers who work remotely over Zoom or Google Meet."
        image={HERO} testid="online-hero" />

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 space-y-16">
        {isLoading && <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-[#144F75]" size={28} /></div>}

        {empty && (
          <div className="eyw-card p-12 text-center" data-testid="online-empty">
            <Video className="mx-auto mb-3 text-[#8FA89B]" size={34} />
            <h2 className="font-serif text-2xl text-[#1A242B] mb-2">Virtual offerings are on the way</h2>
            <p className="text-[#4A5D6B] max-w-lg mx-auto">We're preparing live online sessions and events. In the meantime, explore our centers or reach out — we'll help you find a virtual fit.</p>
            <Link to="/locations" className="eyw-btn mt-6 inline-flex">Browse our centers</Link>
          </div>
        )}

        {services.length > 0 && (
          <section data-testid="online-services">
            <h2 className="font-serif text-3xl text-[#1A242B] mb-6 flex items-center gap-2"><Video size={24} className="text-[#9E5C7F]" /> Live virtual sessions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s) => (
                <div key={s.id} className="eyw-card overflow-hidden" data-testid={`online-service-${s.id}`}>
                  {s.image && <img src={s.image} alt={s.title} className="h-40 w-full object-cover" />}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="eyw-label">{s.category || "Session"}</p>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#9E5C7F]/12 text-[#9E5C7F] capitalize">{(s.delivery_method || "").replace("_", " ") || "online"}</span>
                    </div>
                    <h3 className="font-serif text-xl text-[#1A242B]">{s.title}</h3>
                    <ListingDescription text={s.short_description || s.description} item={s} className="text-sm text-[#4A5D6B] mt-2 line-clamp-2" testid={`online-desc-${s.id}`} />
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-[#144F75] font-serif text-lg">{s.price > 0 ? `${money(s.price)}` : "Free"} <span className="text-xs text-[#8FA89B]">· {s.duration_min}min</span></span>
                      <button onClick={() => setBookSvc(s)} data-testid={`online-book-${s.id}`} className="eyw-btn !py-2 !px-4 text-sm">Book online</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {events.length > 0 && (
          <section data-testid="online-events">
            <h2 className="font-serif text-3xl text-[#1A242B] mb-6 flex items-center gap-2"><Calendar size={24} className="text-[#144F75]" /> Virtual events</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((e) => (
                <div key={e.id} className="eyw-card p-6" data-testid={`online-event-${e.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="eyw-label">{e.category}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#9E5C7F]/12 text-[#9E5C7F]">Online</span>
                  </div>
                  <h3 className="font-serif text-xl text-[#1A242B]">{e.title}</h3>
                  <p className="text-sm text-[#4A5D6B] mt-2 flex items-center gap-2"><Calendar size={14} /> {fmtDate(e.date)} · {fmtTimeRange(e.start_time)}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-[#144F75] font-serif text-lg">{e.price > 0 ? `${money(e.price)}` : "Free"}</span>
                    <button onClick={() => setEventModal(e)} data-testid={`online-register-${e.id}`} className="eyw-btn !py-2 !px-4 text-sm">Register</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {enhancers.length > 0 && (
          <section data-testid="online-enhancers">
            <h2 className="font-serif text-3xl text-[#1A242B] mb-6 flex items-center gap-2"><Sparkles size={24} className="text-[#5C8A72]" /> Enhancers available online</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {enhancers.map((en) => (
                <Link to={`/enhancers/${en.id}`} key={en.id} className="eyw-card p-6 text-center hover:shadow-lg transition-shadow" data-testid={`online-enhancer-${en.id}`}>
                  {en.avatar ? <img src={en.avatar} alt={en.name} className="h-20 w-20 rounded-full object-cover mx-auto mb-3" /> : <div className="h-20 w-20 rounded-full bg-[#144F75]/10 grid place-items-center mx-auto mb-3"><Sparkles className="text-[#144F75]" /></div>}
                  <h3 className="font-serif text-lg text-[#1A242B]">{en.name}</h3>
                  {en.headline && <p className="text-xs text-[#4A5D6B] mt-1">{en.headline}</p>}
                  <span className="mt-3 inline-block text-sm font-semibold text-[#144F75]" data-testid={`enhancer-card-cta-${en.id}`}>View profile & book →</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {!empty && (
          <div className="text-center">
            <Link to="/locations" className="inline-flex items-center gap-1 text-[#144F75] text-sm font-medium hover:gap-2 transition-all">
              Prefer in person? Find a center <ArrowRight size={15} />
            </Link>
          </div>
        )}
      </div>

      {bookSvc && <BookingModal service={{ ...bookSvc, location_id: "virtual" }} onClose={() => setBookSvc(null)} />}
      {eventModal && <EventModal event={eventModal} onClose={() => setEventModal(null)} />}
    </div>
  );
}
